import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import PageTitle from "../../../layouts/PageTitle";
import { selectUser } from "../../../../store/authSlice";
import {
  appApi,
  useGetNcsModemRecoveryQuery,
  useGetNcsTeamQuery,
} from "../../../../store/appApi";
import {
  usePartnerCode,
  useProjectPartners,
  panelClass,
  fieldClass,
  btnGhost,
  Badge,
  STATUS_TONE,
  MODEM_STATE_LABEL,
  MODEM_STATE_TONE,
  fmtDate,
  fmtDateInput,
} from "./config";
import {
  setModemOutcomeApi,
  addModemRemarkApi,
  handoverModemCaseApi,
  handoverModemCasesBulkApi,
  reopenModemCaseApi,
  cancelModemTransferApi,
  markCaseUntraceableApi,
  downloadWorkbook,
} from "../../../../services/netplusCustomerStatus";
import { uiToast, uiPrompt, uiAlert, uiConfirm } from "./ui";

const errMsg = (e) => e?.response?.data?.message || "Something went wrong.";
const DAY_MS = 24 * 60 * 60 * 1000;

// Has the CURRENT assignee done anything since the case landed on them —
// left a remark, logged a field visit, or closed it out? `assignedAt` resets
// on every hand-off (initial transfer, an "untraceable" reassignment, or a
// reopen — see openModemRecoveryCase/markCaseUntraceable/reopenModemCase on
// the backend) so a remark left by a *previous* assignee doesn't count.
const actionedSinceAssignment = (c) => {
  if (!c.isOpen) return true; // closed is itself the final action
  if (!c.assignedAt) return true; // legacy case, predates this tracking
  const since = new Date(c.assignedAt).getTime();
  const remarked = (c.remarks || []).some((r) => new Date(r.at).getTime() >= since);
  const visited = (c.visits || []).some((v) => new Date(v.at).getTime() >= since);
  return remarked || visited;
};

// Tabs that list recovered modems — bulk hand-over selection is offered here.
const RECOVERED_TABS = ["recovered", "recharged", "recovered-sheet", ""];

const API_ORIGIN = process.env.REACT_APP_API_URL || "http://localhost:5001";
const resolveUrl = (u) =>
  !u ? null : u.startsWith("http") ? u : `${API_ORIGIN}${u}`;

// "Rajesh Kumar" -> "RK".
const initialsOf = (name) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "—";

const authorChip = (by, byName, meId) =>
  by && by._id && meId && String(by._id) === String(meId)
    ? "You"
    : initialsOf((by && by.name) || byName);

// Merge remarks + field visits into one activity trail, newest first.
const activityOf = (c) => {
  const remarks = (c?.remarks || []).map((r) => ({
    at: r.at,
    by: r.by,
    byName: r.byName,
    text: r.text,
    kind: "remark",
  }));
  const visits = (c?.visits || []).map((v) => ({
    at: v.at,
    by: v.by,
    byName: v.byName,
    text: v.note,
    photoUrl: v.photoUrl,
    kind: "visit",
  }));
  return [...remarks, ...visits].sort(
    (a, b) => new Date(b.at || 0) - new Date(a.at || 0),
  );
};

const isRecoveredCase = (c) =>
  ["recovered", "recharged"].includes(c.state) || c.modemAlreadyRecovered;

const STATE_TABS = [
  { key: "open", label: "Open" },
  { key: "recovered", label: "Recovered" },
  { key: "recharged", label: "Recharged" },
  { key: "untraceable", label: "Untraceable" },
  { key: "recovered-sheet", label: "Recovered Modems" },
  { key: "handed-over", label: "Handed Over" },
  { key: "", label: "All" },
];

const NcsModemRecovery = () => {
  const dispatch = useDispatch();
  const me = useSelector(selectUser);
  const meId = me?.localId;
  const isSuperAdmin = me?.role === "super-admin";
  const [historyCase, setHistoryCase] = useState(null);
  const [partnerCode, setPartnerCode] = usePartnerCode();
  const { codes: allPartnerCodes } = useProjectPartners();
  // Local to this screen only — never written to the shared, cross-page
  // selected partner (usePartnerCode/localStorage). Bulk hand-over and the
  // Excel bulk-action upload both need one real partner, so they're hidden
  // while this is on — per-row actions (Recharge/Recovered/Untraceable/
  // Remark/Hand over/Reopen) already work by case id alone and need no
  // change.
  const [viewAll, setViewAll] = useState(false);
  const [state, setState] = useState("open");
  const [q, setQ] = useState("");
  const [qLive, setQLive] = useState("");
  const [name, setName] = useState("");
  const [nameLive, setNameLive] = useState("");
  const [assignedByFilter, setAssignedByFilter] = useState("");
  const [assignedToFilter, setAssignedToFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);

  // Bulk hand-over selection (recovered tabs only).
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const bulkMode = RECOVERED_TABS.includes(state) && !viewAll;
  const toggleSel = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const { data, isFetching } = useGetNcsModemRecoveryQuery({
    partnerCode: viewAll ? "ALL" : partnerCode,
    state,
    assignedBy: assignedByFilter,
    assignedTo: assignedToFilter,
    name: nameLive,
    q: qLive,
    from: fromDate,
    to: toDate,
    page,
    limit: 50,
  });
  const rows = data?.cases || [];
  const totalPages = data?.totalPages || 1;
  const [noActionOnly, setNoActionOnly] = useState(false);
  const visibleRows = noActionOnly
    ? rows.filter((c) => c.isOpen && c.assignedTo && !actionedSinceAssignment(c))
    : rows;
  const refresh = () =>
    dispatch(appApi.util.invalidateTags(["NetplusCustomerStatus"]));

  const { data: teamData } = useGetNcsTeamQuery();
  const staff = (teamData?.members || [])
    .filter((m) => m.active !== false)
    .map((m) => ({ _id: m.userId?._id || m.userId, name: m.userId?.name || m.name }));

  // Modems held per employee under the current tab / partner / date filters
  // (from the server, so it covers every page — not just the rows on screen).
  const assigneeStats = (data?.assigneeCounts || [])
    .filter((a) => a.userId)
    .map((a) => ({
      _id: String(a.userId),
      name: staff.find((s) => String(s._id) === String(a.userId))?.name || "Unknown",
      count: a.count,
    }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  const unassignedCount = (data?.assigneeCounts || []).find((a) => !a.userId)?.count || 0;

  // One inline text box per row, reused for both "Remark" and the "Recharge" /
  // "Recovered" / "Untraceable" outcome note. `mode` decides what Save does.
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState("");
  const getDraft = (id) =>
    drafts[id] || {
      mode: null,
      text: "",
      file: null,
      assignedTo: "",
      handoverAt: fmtDateInput(),
    };
  const openDraft = (id, mode) =>
    setDrafts((p) => ({
      ...p,
      [id]: { mode, text: "", file: null, assignedTo: "", handoverAt: fmtDateInput() },
    }));
  const setText = (id, text) =>
    setDrafts((p) => ({ ...p, [id]: { ...getDraft(id), text } }));
  const setFile = (id, file) =>
    setDrafts((p) => ({ ...p, [id]: { ...getDraft(id), file } }));
  const setAssignedTo = (id, assignedTo) =>
    setDrafts((p) => ({ ...p, [id]: { ...getDraft(id), assignedTo } }));
  const setHandoverAt = (id, handoverAt) =>
    setDrafts((p) => ({ ...p, [id]: { ...getDraft(id), handoverAt } }));
  const closeDraft = (id) =>
    setDrafts((p) => ({
      ...p,
      [id]: { mode: null, text: "", file: null, assignedTo: "", handoverAt: fmtDateInput() },
    }));

  const save = async (c) => {
    const d = getDraft(c._id);
    const text = d.text.trim();
    if (d.mode === "remark") {
      if (!text) return;
      try {
        setSaving(c._id);
        await addModemRemarkApi(c._id, text, partnerCode);
        closeDraft(c._id);
        refresh();
      } catch (e) {
        uiToast({ type: "error", title: "Couldn't save remark", message: errMsg(e) });
      } finally {
        setSaving("");
      }
      return;
    }
    if (d.mode === "untraceable") {
      if (!d.assignedTo) {
        uiToast({ type: "warning", title: "Pick a team member to assign this to." });
        return;
      }
      try {
        setSaving(c._id);
        await markCaseUntraceableApi(
          c._id,
          { note: text, photo: d.file || undefined, assignedTo: d.assignedTo },
          partnerCode,
        );
        closeDraft(c._id);
        refresh();
        uiToast({ type: "success", title: "Marked untraceable — reassigned" });
      } catch (e) {
        uiToast({ type: "error", title: "Couldn't update case", message: errMsg(e) });
      } finally {
        setSaving("");
      }
      return;
    }
    if (d.mode === "handover") {
      try {
        setSaving(c._id);
        await handoverModemCaseApi(
          c._id,
          text,
          partnerCode,
          isSuperAdmin ? d.handoverAt || undefined : undefined,
        );
        closeDraft(c._id);
        refresh();
        uiToast({ type: "success", title: "Modem handed over" });
      } catch (e) {
        uiToast({ type: "error", title: "Couldn't hand over", message: errMsg(e) });
      } finally {
        setSaving("");
      }
      return;
    }
    // outcome: recharged | recovered (untraceable is handled above — it
    // reassigns instead of closing, see markCaseUntraceableApi)
    if (d.mode === "recharged" && !text) {
      uiToast({ type: "warning", title: "Add a remark before marking recharged." });
      return;
    }
    try {
      setSaving(c._id);
      await setModemOutcomeApi(
        c._id,
        { outcome: d.mode, note: text },
        partnerCode,
      );
      closeDraft(c._id);
      refresh();
      if (d.mode === "recharged")
        uiToast({ type: "success", title: "Marked recharged — customer reactivated" });
      if (d.mode === "recovered") {
        // Stay on the current tab (usually "Open") instead of jumping to
        // "Recovered" — the row just drops out of this tab's list on refresh,
        // so the user can keep working through Open one customer after
        // another without navigating back each time.
        uiToast({
          type: "success",
          title: "Modem recovered",
          message: 'Open the "Hand over" button on this row to submit it to the company.',
        });
      }
    } catch (e) {
      const body = e?.response?.data;
      if (e?.response?.status === 409 && body?.reason === "customer_not_active") {
        refresh();
        await uiAlert({
          type: "warning",
          title: "Customer isn't active yet",
          message: `Customer is still ${body.customerStatus} on the latest sheet — can't mark as recharged until the sheet confirms them Active.`,
        });
        return;
      }
      if (e?.response?.status === 409 && body?.reason === "already_closed") {
        closeDraft(c._id);
        refresh();
        await uiAlert({
          type: "info",
          title: "Already actioned",
          message: `${body.closedByName || "Another employee"} already marked this modem ${body.state} — no further action needed.`,
        });
        return;
      }
      uiToast({ type: "error", title: "Couldn't update case", message: errMsg(e) });
    } finally {
      setSaving("");
    }
  };

  // Wrong transfer? Take the customer out of Modem Recovery and back onto the
  // normal customer / follow-up flow. Available from Pending, Recovered and
  // Untraceable (not once the modem is handed over, or for Recharged).
  const canMoveToActive = (c) => !c.handedOver && c.state !== "recharged";
  const moveToActive = async (c) => {
    const ok = await uiConfirm({
      title: `Move ${c.name || c.userId} back to active customers?`,
      message:
        "Removes this modem-recovery case (it won't count toward anyone's transfers or outcomes) and puts the customer back on the normal follow-up flow. Use this when they were transferred by mistake.",
      confirmText: "Move back",
    });
    if (!ok) return;
    try {
      await cancelModemTransferApi(c._id, partnerCode);
      refresh();
      uiToast({ type: "success", title: "Moved back to active customers" });
    } catch (e) {
      uiToast({ type: "error", title: "Couldn't move back", message: errMsg(e) });
    }
  };

  const reopen = async (c) => {
    try {
      await reopenModemCaseApi(c._id, partnerCode);
      refresh();
    } catch (e) {
      uiToast({ type: "error", title: "Couldn't reopen", message: errMsg(e) });
    }
  };

  const bulkHandover = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    let note = "";
    let handedOverAt;
    if (isSuperAdmin) {
      const res = await uiPrompt({
        title: `Hand over ${ids.length} modem(s)`,
        confirmText: "Hand over all",
        fields: [
          {
            name: "note",
            label: "Handover note (optional)",
            type: "textarea",
          },
          {
            name: "handedOverAt",
            label: "Handed over on (back-date if needed)",
            type: "date",
            initial: fmtDateInput(),
            max: fmtDateInput(),
          },
        ],
      });
      if (res === null) return;
      note = res.note || "";
      handedOverAt = res.handedOverAt || undefined;
    }
    try {
      setBulkBusy(true);
      const { data: res } = await handoverModemCasesBulkApi(
        ids,
        note,
        partnerCode,
        handedOverAt,
      );
      setSelected(new Set());
      refresh();
      uiToast({
        type: "success",
        title: `Handed over ${res?.handedOver ?? ids.length} modem(s)`,
        message: res?.skipped?.length
          ? `${res.skipped.length} skipped`
          : undefined,
      });
    } catch (e) {
      uiToast({ type: "error", title: "Bulk hand-over failed", message: errMsg(e) });
    } finally {
      setBulkBusy(false);
    }
  };

  const selectableRows = rows.filter(
    (c) => isRecoveredCase(c) && !c.handedOver,
  );
  const allSelected =
    selectableRows.length > 0 &&
    selectableRows.every((c) => selected.has(c._id));
  const toggleAll = () =>
    setSelected(
      allSelected ? new Set() : new Set(selectableRows.map((c) => c._id)),
    );

  return (
    <>
      <PageTitle activeMenu="Modem Recovery" motherMenu="Netplus" />
      <section className={panelClass}>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-gray-100 px-5 py-3">
          <div className="flex flex-wrap items-center gap-1.5">
            {STATE_TABS.map((t) => (
              <button
                key={t.key || "all"}
                onClick={() => {
                  setState(t.key);
                  setPage(1);
                  setSelected(new Set());
                }}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                  state === t.key
                    ? "bg-cyan-600 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
            <input
              type="checkbox"
              checked={noActionOnly}
              onChange={(e) => setNoActionOnly(e.target.checked)}
            />
            No action only
            {noActionOnly && (
              <span className="rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold text-rose-700">
                {visibleRows.length}
              </span>
            )}
          </label>
          <form
            className="ml-auto flex flex-wrap items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setQLive(q.trim());
              setNameLive(name.trim());
              setPage(1);
            }}
          >
            <label
              className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-300 bg-white px-2 py-1"
              style={{ backgroundColor: "#fff" }}
              title="Partner code"
            >
              <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                Partner
              </span>
              <select
                className="bg-white text-xs font-semibold text-gray-800 outline-none"
                style={{ backgroundColor: "#fff", color: "#1f2937" }}
                value={viewAll ? "ALL" : partnerCode}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "ALL") {
                    setViewAll(true);
                  } else {
                    setViewAll(false);
                    setPartnerCode(v);
                  }
                  setPage(1);
                  setSelected(new Set());
                }}
              >
                <option value="ALL" style={{ color: "#1f2937", backgroundColor: "#fff" }}>
                  All partners
                </option>
                {(allPartnerCodes.length ? allPartnerCodes : [partnerCode]).map((c) => (
                  <option key={c} value={c} style={{ color: "#1f2937", backgroundColor: "#fff" }}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <input
              className={`${fieldClass} w-36`}
              placeholder="Customer name"
              title="Filter by customer name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <select
              className={fieldClass}
              title="Show only the modems assigned to this employee"
              value={assignedToFilter}
              onChange={(e) => {
                setAssignedToFilter(e.target.value);
                setPage(1);
                setSelected(new Set());
              }}
            >
              <option value="">Assigned to (all)</option>
              {assigneeStats.map((a) => (
                <option key={a._id} value={a._id}>
                  {a.name} ({a.count})
                </option>
              ))}
              {assignedToFilter && !assigneeStats.some((a) => a._id === assignedToFilter) && (
                <option value={assignedToFilter}>
                  {staff.find((s) => String(s._id) === assignedToFilter)?.name || "Selected"} (0)
                </option>
              )}
            </select>
            <select
              className={fieldClass}
              title="Assigned by"
              value={assignedByFilter}
              onChange={(e) => {
                setAssignedByFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Assigned by (all)</option>
              {staff.map((s) => (
                <option key={s._id} value={s._id}>
                  {s.name}
                </option>
              ))}
            </select>
            <input
              type="date"
              className={fieldClass}
              title="Date of case assigned — from"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setPage(1);
              }}
            />
            <span className="text-xs text-gray-400">to</span>
            <input
              type="date"
              className={fieldClass}
              title="Date of case assigned — to"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setPage(1);
              }}
            />
            <input
              className={`${fieldClass} w-full min-w-[10rem] sm:w-56`}
              placeholder="Phone / id / MAC"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <button className={btnGhost} type="submit">
              Search
            </button>
            <button
              type="button"
              className="rounded-lg bg-cyan-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-cyan-500"
              onClick={() =>
                downloadWorkbook(partnerCode, {
                  view:
                    state === "recovered-sheet"
                      ? "modem-recovered"
                      : "modem-recovery",
                })
              }
            >
              Download Excel Sheet
            </button>
          </form>
          {(assigneeStats.length > 0 || unassignedCount > 0) && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                Modems per employee
              </span>
              {assigneeStats.map((a) => (
                <button
                  key={a._id}
                  type="button"
                  onClick={() => {
                    setAssignedToFilter(assignedToFilter === a._id ? "" : a._id);
                    setPage(1);
                    setSelected(new Set());
                  }}
                  className={`rounded-full border px-2.5 py-0.5 font-medium ${
                    assignedToFilter === a._id
                      ? "border-cyan-500 bg-cyan-600 text-white"
                      : "border-gray-200 bg-gray-50 text-gray-700 hover:border-cyan-400"
                  }`}
                >
                  {a.name} · {a.count}
                </button>
              ))}
              {unassignedCount > 0 && (
                <span className="rounded-full border border-gray-200 px-2.5 py-0.5 text-gray-400">
                  Unassigned · {unassignedCount}
                </span>
              )}
            </div>
          )}
        </div>

        {bulkMode && (
          <div className="flex flex-wrap items-center gap-3 border-b border-gray-100 bg-emerald-50/60 px-5 py-2 text-xs">
            <span className="font-semibold text-emerald-900">
              Transfer to Hand Over
            </span>
            {selectableRows.length === 0 ? (
              <span className="text-gray-400">
                No recovered modem on this tab is pending hand-over.
              </span>
            ) : (
              <>
                <span className="text-emerald-800">{selected.size} selected</span>
                <button
                  type="button"
                  className="rounded-lg border border-emerald-300 px-2 py-1 font-medium text-emerald-700 hover:bg-emerald-100"
                  onClick={toggleAll}
                >
                  {allSelected ? "Clear page" : "Select page"}
                </button>
                <button
                  type="button"
                  className="rounded-lg bg-emerald-600 px-3 py-1.5 font-semibold text-white transition hover:bg-emerald-500 disabled:opacity-50"
                  onClick={bulkHandover}
                  disabled={bulkBusy || selected.size === 0}
                >
                  {bulkBusy
                    ? "…"
                    : `Hand over ${selected.size || ""} recovered modem${
                        selected.size === 1 ? "" : "s"
                      }`.trim()}
                </button>
                <span className="text-gray-400">
                  Tick recovered modems and transfer them to the Hand Over sheet.
                </span>
              </>
            )}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                {bulkMode && (
                  <th className="px-3 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      aria-label="Select all on page"
                    />
                  </th>
                )}
                {viewAll && (
                  <th className="whitespace-nowrap px-4 py-3 text-left">Partner</th>
                )}
                <th className="px-4 py-3 text-left" style={{ minWidth: 200 }}>
                  Customer
                </th>
                <th className="whitespace-nowrap px-4 py-3 text-left">MAC</th>
                <th className="whitespace-nowrap px-4 py-3 text-center">
                  Connection
                </th>
                <th className="whitespace-nowrap px-4 py-3 text-center">
                  Overdue
                </th>
                <th
                  className="whitespace-nowrap px-4 py-3 text-center"
                  style={{ minWidth: 110 }}
                >
                  Transferred
                </th>
                <th className="px-4 py-3 text-left" style={{ minWidth: 130 }}>
                  Assigned to
                </th>
                <th className="whitespace-nowrap px-4 py-3 text-center">
                  State
                </th>
                <th className="px-4 py-3 text-left" style={{ minWidth: 160 }}>
                  Reason / outcome
                </th>
                <th className="whitespace-nowrap px-4 py-3 text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isFetching ? (
                <tr>
                  <td
                    colSpan={9 + (bulkMode || viewAll ? 1 : 0)}
                    className="px-4 py-12 text-center text-gray-400"
                  >
                    Loading…
                  </td>
                </tr>
              ) : visibleRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={9 + (bulkMode || viewAll ? 1 : 0)}
                    className="px-4 py-12 text-center text-gray-400"
                  >
                    {noActionOnly ? "No unactioned cases — nice." : "No cases."}
                  </td>
                </tr>
              ) : (
                visibleRows.map((c) => {
                  const d = getDraft(c._id);
                  const selectable = isRecoveredCase(c) && !c.handedOver;
                  return (
                    <tr key={c._id} className="align-top hover:bg-cyan-50/30">
                      {bulkMode && (
                        <td className="px-3 py-2.5 text-center">
                          {selectable && (
                            <input
                              type="checkbox"
                              checked={selected.has(c._id)}
                              onChange={() => toggleSel(c._id)}
                              aria-label="Select for hand-over"
                            />
                          )}
                        </td>
                      )}
                      {viewAll && (
                        <td className="px-4 py-2.5 text-xs font-semibold text-gray-500">
                          {c.partnerCode}
                        </td>
                      )}
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-gray-800">
                          {c.name || c.userId}
                        </div>
                        <div
                          className="flex flex-wrap gap-x-1 font-bold"
                          style={{ color: "#222222", fontSize: 14 }}
                        >
                          <span className="whitespace-nowrap">m:{c.phone || "—"},</span>
                          <span className="whitespace-nowrap">id:{c.userId}</span>
                        </div>
                        {c.customerAddress && (
                          <div className="mt-0.5 text-xs text-gray-500">
                            {c.customerAddress}
                          </div>
                        )}
                        {c.reactivatedCustomer && (
                          <div className="mt-0.5 text-xs font-medium text-cyan-700">
                            Reactivated
                            {c.assignedTo?.name ? ` · ${c.assignedTo.name}` : ""}
                          </div>
                        )}
                        {c.handedOver && (
                          <div className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800">
                            Handed over · {fmtDate(c.handedOverAt)}
                          </div>
                        )}
                        {(() => {
                          const hist = activityOf(c);
                          const last = hist[0];
                          if (!last)
                            return (
                              <div className="mt-1 text-xs text-gray-400">
                                No remarks yet
                              </div>
                            );
                          return (
                            <button
                              type="button"
                              onClick={() => setHistoryCase(c)}
                              title="View all remarks & visits for this customer"
                              className="mt-1 flex w-full max-w-[340px] items-center gap-1.5 text-left text-[13px] transition hover:opacity-75"
                            >
                              <span className="shrink-0 rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold text-slate-700">
                                {authorChip(last.by, last.byName, meId)}
                              </span>
                              <span
                                className="shrink-0 whitespace-nowrap font-semibold text-gray-500"
                              >
                                {fmtDate(last.at)}
                              </span>
                              {last.kind === "visit" && (
                                <span className="shrink-0 rounded bg-indigo-100 px-1 text-[9px] font-bold uppercase text-indigo-700">
                                  visit
                                </span>
                              )}
                              <span
                                className="min-w-0 flex-1 truncate font-medium"
                                style={{ color: "#1d4ed8" }}
                              >
                                {last.text || "(no remark)"}
                              </span>
                              {hist.length > 1 && (
                                <span className="shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                                  {hist.length}
                                </span>
                              )}
                            </button>
                          );
                        })()}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-xs text-gray-500">
                        {c.macId || (
                          <span className="text-gray-400">
                            {c.modemAlreadyRecovered ? "already recovered" : "—"}
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-center">
                        {c.customerStatus ? (
                          <Badge tone={STATUS_TONE[c.customerStatus]}>
                            {c.customerStatus}
                          </Badge>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-center tabular-nums">
                        {c.daysOverdue != null ? (
                          <span className="text-rose-600">{c.daysOverdue}d</span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-center text-gray-500">
                        {fmtDate(c.transferredOn)}
                        <div className="whitespace-nowrap text-[11px] text-gray-400">
                          {c.transferredBy?.name || "—"}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-700">
                        <div className="whitespace-nowrap">
                          {c.assignedTo?.name || (
                            <span className="text-gray-400">Unassigned</span>
                          )}
                        </div>
                        {c.isOpen && c.assignedTo && c.assignedAt && (
                          <div className="whitespace-nowrap text-[11px] text-gray-400">
                            since {fmtDate(c.assignedAt)}
                          </div>
                        )}
                        {c.isOpen && c.assignedTo && !actionedSinceAssignment(c) && (
                          <div className="mt-1">
                            <Badge tone="bg-rose-50 text-rose-700 border-rose-200">
                              No action
                              {c.assignedAt
                                ? ` · ${Math.max(0, Math.floor((Date.now() - new Date(c.assignedAt).getTime()) / DAY_MS))}d`
                                : ""}
                            </Badge>
                          </div>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-center">
                        <Badge tone={MODEM_STATE_TONE[c.state]}>
                          {MODEM_STATE_LABEL[c.state] || c.state}
                        </Badge>
                        {!c.isOpen && (c.closedBy?.name || c.closedOn) && (
                          <div className="mt-1 text-[11px] text-gray-500">
                            by {c.closedBy?.name || "—"}
                            {c.closedOn ? ` · ${fmtDate(c.closedOn)}` : ""}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-500">
                        {c.transferReason || "—"}
                        {c.outcomeNote ? ` · ${c.outcomeNote}` : ""}
                        {(c.remarks || []).length > 0 && (
                          <div className="mt-1 space-y-0.5 text-gray-400">
                            {c.remarks.slice(-3).map((r, i) => (
                              <div key={i}>
                                {fmtDate(r.at)}: {r.text}
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="flex flex-col items-end gap-1.5">
                          <div className="inline-flex flex-wrap justify-end gap-1">
                            {c.isOpen ? (
                              <>
                                <button
                                  className="rounded-lg bg-cyan-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-cyan-500"
                                  onClick={() => openDraft(c._id, "recharged")}
                                >
                                  Recharge
                                </button>
                                <button
                                  className={btnGhost}
                                  onClick={() => openDraft(c._id, "recovered")}
                                >
                                  Recovered
                                </button>
                                <button
                                  className={btnGhost}
                                  onClick={() => openDraft(c._id, "untraceable")}
                                >
                                  Untraceable
                                </button>
                                <button
                                  className={btnGhost}
                                  onClick={() => openDraft(c._id, "remark")}
                                >
                                  Remark
                                </button>
                                {canMoveToActive(c) && (
                                  <button
                                    className="rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-700 transition hover:bg-rose-50"
                                    onClick={() => moveToActive(c)}
                                  >
                                    Move to active
                                  </button>
                                )}
                              </>
                            ) : (
                              <>
                                {isRecoveredCase(c) && !c.handedOver && (
                                  <button
                                    className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-500"
                                    onClick={() => openDraft(c._id, "handover")}
                                  >
                                    Hand over
                                  </button>
                                )}
                                {!c.handedOver && (
                                  <button className={btnGhost} onClick={() => reopen(c)}>
                                    Reopen
                                  </button>
                                )}
                                {canMoveToActive(c) && (
                                  <button
                                    className="rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-700 transition hover:bg-rose-50"
                                    onClick={() => moveToActive(c)}
                                  >
                                    Move to active
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                          {d.mode && (
                            <div className="flex flex-col items-end gap-1.5">
                              {d.mode === "untraceable" && (
                                <>
                                  <select
                                    className={`${fieldClass} w-44`}
                                    value={d.assignedTo}
                                    onChange={(e) => setAssignedTo(c._id, e.target.value)}
                                  >
                                    <option value="">Assign to…</option>
                                    {staff.map((s) => (
                                      <option key={s._id} value={s._id}>
                                        {s.name}
                                      </option>
                                    ))}
                                  </select>
                                  <label className="cursor-pointer text-xs text-cyan-700">
                                    <input
                                      type="file"
                                      accept="image/*"
                                      capture="environment"
                                      className="hidden"
                                      onChange={(e) =>
                                        setFile(c._id, e.target.files?.[0] || null)
                                      }
                                    />
                                    {d.file
                                      ? `📎 ${d.file.name}`
                                      : "📷 Attach photo (optional)"}
                                  </label>
                                </>
                              )}
                              <div className="flex items-center gap-1.5">
                                {d.mode === "handover" && isSuperAdmin && (
                                  <input
                                    type="date"
                                    title="Handed over on (back-date if needed)"
                                    className={`${fieldClass} w-36`}
                                    value={d.handoverAt}
                                    max={fmtDateInput()}
                                    onChange={(e) =>
                                      setHandoverAt(c._id, e.target.value)
                                    }
                                  />
                                )}
                                <input
                                  autoFocus
                                  className={`${fieldClass} w-56`}
                                  placeholder={
                                    d.mode === "remark"
                                      ? "Add a remark…"
                                      : d.mode === "untraceable"
                                        ? "What happened? (optional)"
                                        : d.mode === "recharged"
                                          ? "Payment details (required)"
                                          : d.mode === "handover"
                                            ? "Handover note (optional)"
                                            : "note (optional)"
                                  }
                                  value={d.text}
                                  onChange={(e) => setText(c._id, e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") save(c);
                                    if (e.key === "Escape") closeDraft(c._id);
                                  }}
                                />
                                <button
                                  className="rounded-lg bg-cyan-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-cyan-500 disabled:opacity-50"
                                  disabled={saving === c._id}
                                  onClick={() => save(c)}
                                >
                                  {saving === c._id
                                    ? "…"
                                    : d.mode === "remark"
                                      ? "Save"
                                      : d.mode === "untraceable"
                                        ? "Mark untraceable"
                                        : d.mode === "handover"
                                          ? "Confirm handover"
                                          : `Mark ${MODEM_STATE_LABEL[d.mode] || d.mode}`}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-5 py-3 text-xs text-gray-500">
          <span>{data?.totalCount ?? 0} total</span>
          <div className="flex items-center gap-2">
            <button
              className={btnGhost}
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Prev
            </button>
            <span>
              {page} / {totalPages}
            </span>
            <button
              className={btnGhost}
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </section>

      {historyCase && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => setHistoryCase(null)}
        >
          <div
            className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {historyCase.name || historyCase.userId}
                </h3>
                <p
                  className="mt-0.5 flex flex-wrap gap-x-1 font-bold"
                  style={{ color: "#222222", fontSize: 14 }}
                >
                  <span className="whitespace-nowrap">m:{historyCase.phone || "—"},</span>
                  <span className="whitespace-nowrap">id:{historyCase.userId}</span>
                </p>
              </div>
              <button
                type="button"
                className="rounded-full border-2 border-gray-300 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-gray-600"
                onClick={() => setHistoryCase(null)}
              >
                Close
              </button>
            </div>
            <div className="mt-4 max-h-96 overflow-y-auto rounded-lg border border-gray-200">
              {activityOf(historyCase).map((h, i) => (
                <div
                  key={i}
                  className={`px-4 py-2 text-sm ${i % 2 === 1 ? "bg-gray-50" : "bg-white"}`}
                  style={{ color: h.kind === "visit" ? "#3730a3" : "#1d4ed8" }}
                >
                  <span className="font-semibold">{fmtDate(h.at)}</span>{" "}
                  <span
                    className="rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold text-slate-700"
                    title={(h.by && h.by.name) || h.byName || ""}
                  >
                    {authorChip(h.by, h.byName, meId)}
                  </span>{" "}
                  <span className="text-xs opacity-70">[{h.kind}]</span>{" "}
                  <span className="font-medium">— {h.text || "(no remark)"}</span>
                  {h.photoUrl && (
                    <a
                      href={resolveUrl(h.photoUrl)}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-1 block"
                    >
                      <img
                        src={resolveUrl(h.photoUrl)}
                        alt="visit"
                        className="max-h-40 rounded-lg border border-gray-200"
                      />
                    </a>
                  )}
                </div>
              ))}
              {activityOf(historyCase).length === 0 && (
                <div className="px-4 py-6 text-center text-xs text-gray-400">
                  No remarks or visits logged yet.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NcsModemRecovery;
